// "Find a pallet or bin" on /coldroom — a typed value or a scan, answered with
// where it stands (PM-CST-04, PB-11). The lookup rules live in
// lib/coldroomFind.ts; this renders the field, listens for the scanner, and
// hands a hit to the page, which switches room and opens the cell.
import { useState } from 'react'
import clsx from 'clsx'
import { ScanLine } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { useScanWedge } from '@/hooks/useScanWedge'
import { playScanBeep } from '@/lib/tone'
import { findInColdroom, hitSentence, type ColdroomHit, type FindReads } from '@/lib/coldroomFind'

const PALLET_COLS = 'label:pallet_id, status, coldroom_id, coldroom_grid, coldroom_overflow, version, tenant_id, awaiting_weight, loading_bay_lane, runs(run_kind, variety), mission:loading_missions!pallets_loading_mission_id_fkey(mission_number)'
const BIN_COLS    = 'label:bin_number, status, coldroom_id, coldroom_grid, coldroom_overflow'

export const READS: FindReads = {
  palletById:   p => withReadTimeout(supabase.from('pallets').select(PALLET_COLS).ilike('pallet_id', p).is('deleted_at', null).limit(1), 'ColdroomFind: pallet id'),
  palletBySscc: s => withReadTimeout(supabase.from('pallets').select(PALLET_COLS).eq('ssc_id', s).is('deleted_at', null).limit(1), 'ColdroomFind: sscc'),
  palletByClientSscc: c => withReadTimeout(supabase.from('pallets').select(PALLET_COLS).in('sscc', c).is('deleted_at', null).limit(1), 'ColdroomFind: client sscc'),
  binByNumber:  p => withReadTimeout(supabase.from('bins').select(BIN_COLS).ilike('bin_number', p).is('deleted_at', null).limit(1), 'ColdroomFind: bin'),
}

const TONE: Record<ColdroomHit['kind'], string> = {
  cell: 'text-success', overflow: 'text-success', nocell: 'text-warning', awaiting: 'text-success',
  elsewhere: 'text-warning', none: 'text-warning', error: 'text-danger',
}

// A scan is a run of 5+ characters with no space, ended by the reader's Enter.
// No pallet or bin format is assumed — numbering is per tenant.
const scanShape = (buf: string) => { const t = buf.trim(); return t.length >= 5 && !/\s/.test(t) ? t : null }

export default function ColdroomFind({ roomCode, onHit, onAwaiting, reads = READS }: {
  roomCode: (roomId: string) => string
  /** A hit the page can show: switch room, open the cell / scroll to the overflow. */
  onHit:    (hit: Extract<ColdroomHit, { kind: 'cell' | 'overflow' }>) => void
  /** A pallet still in production: the page opens its Place sheet (F05 UF-01). */
  onAwaiting?: (hit: Extract<ColdroomHit, { kind: 'awaiting' }>) => void
  /** The three lookups — the real queries unless a test hands in its own. */
  reads?:   FindReads
}) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ColdroomHit | null>(null)

  async function find(raw: string) {
    if (!raw.trim()) return
    setBusy(true)
    const hit = await findInColdroom(raw, reads)
    setBusy(false)
    setResult(hit)
    if (hit.kind === 'cell' || hit.kind === 'overflow') {
      playScanBeep()
      setText('')
      onHit(hit)
    }
    if (hit.kind === 'nocell') {
      // Same landing as the overflow: switch room, scroll to the Place-it list.
      playScanBeep()
      setText('')
      onHit({ kind: 'overflow', what: hit.what, label: hit.label, roomId: hit.roomId })
    }
    if (hit.kind === 'awaiting' && onAwaiting) {
      playScanBeep()
      setText('')
      onAwaiting(hit)
    }
  }

  // A scan with no field focused (the field itself handles its own Enter).
  useScanWedge(() => null, raw => { void find(raw) }, { matchOnFlush: scanShape })

  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2 min-h-[44px] rounded-xl surface border hairline-strong px-3 cursor-text">
        <ScanLine size={16} className="text-fg-muted shrink-0" />
        <input className="flex-1 min-w-0 self-stretch bg-transparent text-sm text-fg outline-none"
          aria-label="Find a pallet or bin — type or scan"
          placeholder="Find a pallet or bin — type or scan"
          autoComplete="off" value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void find(text) } }} />
        {busy && <span className="text-caption text-fg-muted">Looking…</span>}
      </label>
      {result && (
        <p role="status" className={clsx('text-caption px-1', TONE[result.kind])}>
          {hitSentence(result, roomCode)}
        </p>
      )}
    </div>
  )
}
